/* eslint-disable react/no-array-index-key */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Cell, Formula, TableEditorProps, TableEditorRef } from "./dto";
import BorderRect from "./plugins/border-rect";
import ResizeGrid from "./plugins/resize-grid";
import useSelectionBorderRect from "./hooks/use-selection-border-rect";
import useLoop from "./hooks/use-loop";
import {
  getFieldNamePath,
  getStyleByCell,
  initTableConfig,
  isField,
  isVaildFormila,
} from "./utils";
import useResizeGrid from "./hooks/use-resize-grid";
import { useSettlementStore } from "./store";
import { DATA_TYPE, EMPTY_TMPL, FORMULA_TYPE } from "./contanst";
import LoopSelect from "./plugins/loop-select";
import useFlattenFields from "./hooks/use-flatten-fields";
import FormulaSetting from "./plugins/formula-setting";

/**
 * 表格编辑器
 * @param props
 * @param ref
 * @returns
 */
const InnerTableEditor = forwardRef(
  (
    props: Omit<TableEditorProps, "onCellValueChange">,
    ref: React.Ref<TableEditorRef>
  ) => {
    /**
     * 属性结构
     */
    const {
      initialConfig = EMPTY_TMPL,
      fields,
      onResize,
      onSelected,
      onChange,
    } = props;

    const [formulaOpen, setFormulaOpen] = useState(false);

    const tableElementRef = useRef<HTMLTableElement>();

    const [{ config, selection }, { setConfig }] = useSettlementStore();

    const [flds] = useFlattenFields(fields);
    // 单元格选择
    const [setSelectionRef, borderRectProps] = useSelectionBorderRect();
    // 循环
    const [setLoopRef, loopSelectProps, loopBorderRects] = useLoop(flds);
    // 表格缩放
    const [setResizeRef, resizeGridProps] = useResizeGrid(onResize);

    const tableWidth = useMemo(() => {
      return config.cols.reduce((pre, cur) => {
        return pre + cur;
      }, 0);
    }, [config.cols]);

    useEffect(() => {
      setConfig(initTableConfig(initialConfig));
    }, []);

    // 同步系统公式到config
    useEffect(() => {
      // 支持系统公式的字段
      const fieldsFormulas = flds.filter((i) => i.formula.length > 0);
      const fldCodes = flds.map((i) => i.code);
      setConfig((c) => {
        const newSystemFormulas: Formula[] = [];
        const existFormulas = c.formulas.map((i) => i.code);
        for (const field of fieldsFormulas) {
          const idx = existFormulas.indexOf(field.code);
          const formulaValue =
            idx === -1 ? field.formula : c.formulas[idx].value;
          newSystemFormulas.push({
            name: field.name,
            code: field.code,
            // 不存在则使用系统默认公式 否则使用原有公式
            value: formulaValue,
            isList: field.isList,
          });
        }
        // 自定义公式
        c.formulas = c.formulas.filter((i) => i.custom);
        // 更新系统公式
        c.formulas.push(...newSystemFormulas);
        // 移除不存在的公式字段
        c.formulas = c.formulas.filter((i) => i.value.length > 0);
        // 合法的公式
        const validFormulas = c.formulas.filter((i) =>
          isVaildFormila(i, fldCodes)
        );
        // 所有字段code
        const codes = [...fldCodes, ...validFormulas.map((i) => i.code)];
        c.rows.forEach((row) => {
          row.cells.forEach((cell) => {
            const cellCodes =
              cell.value
                ?.filter((i) => isField(i.value, i.type))
                ?.map((i) => i.value) ?? [];
            // 移除不存在的字段配置
            if (cellCodes.some((i) => !codes.includes(i))) {
              cell.value = undefined;
              cell.type = undefined;
            } else {
              // 同步系统字段名称
              cell.value?.forEach((v) => {
                if (!isField(v.value, v.type)) {
                  return;
                }
                const usedField = flds.find((i) => i.code === v.value);
                if (usedField) {
                  v.label = usedField.name;
                }
              });
            }
          });
        });
      });
    }, [flds]);

    useEffect(() => {
      onChange?.(config);
    }, [JSON.stringify(config)]);

    useEffect(() => {
      onSelected?.(selection);
    }, [JSON.stringify(selection)]);

    /**
     * 渲染单元格
     * @param cell 单元格属性
     * @returns
     */
    const renderCell = (cell: Cell) => {
      if (cell.type === DATA_TYPE.DYNAMIC) {
        return `\${${cell.value?.[0]?.label ?? "--"}}`;
      }
      if (cell.type === DATA_TYPE.FORMAT) {
        return cell.value
          ?.map((i) =>
            i.type === FORMULA_TYPE.DYNAMIC ? `\${${i.label}}` : i.label
          )
          .join(" ");
      }
      return "";
    };

    const renderBody = () => {
      return config.rows.map((row) => {
        return (
          <tr
            key={row.id}
            data-id={row.id}
            style={{ height: `${row.height}px` }}
          >
            {row.cells.map((cell) => {
              const Tag = cell.tag ?? "td";
              const fieldNamePath = getFieldNamePath(cell).join("_");
              const nameAttr = fieldNamePath
                ? { ["data-name"]: fieldNamePath }
                : {};
              return (
                <Tag
                  key={cell.id}
                  data-id={cell.id}
                  {...nameAttr}
                  rowSpan={cell.rowSpan}
                  colSpan={cell.colSpan}
                  style={{
                    ...getStyleByCell(cell.style),
                    // @ts-ignore
                    textWrap: "wrap",
                    wordBreak: "break-all",
                  }}
                  className="wbuc-relative"
                  // 使单元格拥有聚焦能力
                  tabIndex={0}
                >
                  {renderCell(cell)}
                </Tag>
              );
            })}
          </tr>
        );
      });
    };

    /**
     * 暴露方法
     */
    useImperativeHandle(ref, () => {
      return {
        getConfig: () => config,
        setConfig,
        getUsedFields: () => {
          // 系统字段code
          const codes: string[] = [];
          // 公式code
          const formulas: string[] = [];
          // 格式化的map
          const parseConfigMap = {};
          // 单元格中使用的code
          for (const row of config.rows) {
            for (const cell of row.cells) {
              cell.value?.forEach((i) => {
                if (!isField(i.value, i.type)) {
                  return;
                }
                // 自定义公式
                const formula = config.formulas.find((j) => j.code === i.value);
                const parse = {
                  roundMode: cell.attr?.rounding,
                  decimalPlaces: cell.attr?.precision,
                };
                if (formula) {
                  formulas.push(formula.code);
                  codes.push(
                    ...formula.value
                      .filter((j) => isField(j.value, j.type))
                      .map((j) => j.value)
                  );
                  parseConfigMap[formula.code] = parse;
                } else {
                  codes.push(i.value);
                  parseConfigMap[i.value] = parse;
                }
              });
            }
          }
          return [
            ...flds
              .filter(
                (i) => codes.includes(i.code) && !formulas.includes(i.code)
              )
              .map((i) => ({ ...i, ...parseConfigMap[i.code] })),
            // 自定义公式
            ...config.formulas
              .filter((i) => formulas.includes(i.code))
              .map((i) => ({
                ...i,
                formula: i.value,
                isList: i.isList ?? false,
                ...parseConfigMap[i.code],
              })),
          ];
        },
        openFormulaSetting: () => setFormulaOpen(true),
        closeFormulaSetting: () => setFormulaOpen(false),
      };
    });

    return (
      <div className="wbuc-size-full wbuc-overflow-auto">
        <div className="wbuc-relative wbuc-ml-[0.625rem] wbuc-w-fit">
          <table
            ref={(node) => {
              tableElementRef.current = node;
              setSelectionRef(node);
              setLoopRef(node);
              setResizeRef(node);
            }}
            className="wbuc-settlement-viewer wbuc-select-none"
            style={{
              width: tableWidth,
            }}
          >
            <colgroup>
              {config.cols.map((col, index) => {
                return (
                  <col key={index} width={`${col}px`} style={{ width: col }} />
                );
              })}
            </colgroup>
            <tbody>{renderBody()}</tbody>
          </table>
          <ResizeGrid {...resizeGridProps} />
          <BorderRect.Container>
            {loopBorderRects.map(({ id, ...rest }) => (
              <BorderRect key={id} {...rest} width={tableWidth} />
            ))}
            <BorderRect {...borderRectProps} />
          </BorderRect.Container>
          <LoopSelect {...loopSelectProps} />
          <FormulaSetting
            open={formulaOpen}
            onOpenChange={(v) => setFormulaOpen(v)}
            fields={fields}
            value={config.formulas}
            onChange={(v) => {
              setConfig((prev) => ({ ...prev, formulas: v }));
            }}
          />
        </div>
      </div>
    );
  }
);

export default InnerTableEditor;
